// header.hooks.ts
import { Dispatch, SetStateAction, useCallback, useState } from "react";

export interface UseHeaderParams {
    onCreateJournal?: (() => void) | undefined;
    onBackNavigate?: (() => void) | undefined;
    showBackButton?: boolean | undefined;
    isChangePasswordTab?: boolean | undefined;
}

export interface UseHeaderResult {
    drawerOpen: boolean;
    urgentOpen: boolean;
    scheduleOpen: boolean;
    handleMenuClick: () => void;
    handleDrawerClose: () => void;
    handleCreateJournal: () => void;
    handleBackClick: () => void;
    handleOpenUrgent: () => void;
    closeAll: () => void;
    setUrgentOpen: Dispatch<SetStateAction<boolean>>;
    setScheduleOpen: Dispatch<SetStateAction<boolean>>;
    shouldShowBack: boolean;
}

export const useHeader = ({
    onCreateJournal,
    onBackNavigate,
    showBackButton = false,
    isChangePasswordTab = false,
}: UseHeaderParams): UseHeaderResult => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [urgentOpen, setUrgentOpen] = useState(false);
    const [scheduleOpen, setScheduleOpen] = useState(false);

    const handleMenuClick = useCallback(() => {
        setDrawerOpen(true);
    }, []);

    const handleDrawerClose = useCallback(() => {
        setDrawerOpen(false);
    }, []);

    const handleCreateJournal = useCallback(() => {
        if (onCreateJournal) {
            onCreateJournal();
        }
        setDrawerOpen(false);
    }, [onCreateJournal]);

    const handleBackClick = useCallback(() => {
        if (onBackNavigate) {
            onBackNavigate();
        }
    }, [onBackNavigate]);

    // Root-level openers so child components can request these modals
    const handleOpenUrgent = useCallback(() => {
        // Open urgent first; underlying drawers can close without affecting it
        setUrgentOpen(true);
    }, []);

    // Used by Cancel in schedule modal to fully close everything
    const closeAll = useCallback(() => {
        setScheduleOpen(false);
        setUrgentOpen(false);
        setDrawerOpen(false);
    }, []);

    const shouldShowBack = !!showBackButton || !!isChangePasswordTab;

    return {
        drawerOpen,
        urgentOpen,
        scheduleOpen,
        handleMenuClick,
        handleDrawerClose,
        handleCreateJournal,
        handleBackClick,
        handleOpenUrgent,
        closeAll,
        setUrgentOpen,
        setScheduleOpen,
        shouldShowBack,
    };
};
